import { Controller } from "@hotwired/stimulus"

// Resolves the selected quality+finish+size to a variant from the embedded
// catalog matrix: shows each size's price for the current quality+finish,
// points the CTA at the configurator, disables sizes with no variant for the
// combination, relabels the finish options to match the selected quality, and
// swaps the caption naming the selected quality+finish paper.
export default class extends Controller {
  static values = { matrix: Object, finishLabels: Object, configuratorUrl: String, unavailableLabel: String }
  static targets = ["size", "finishLabel", "caption", "cta"]

  connect() {
    this.update()
  }

  change(event) {
    if (event && event.target.name === "quality") this.relabelFinishes()
    this.update()
  }

  relabelFinishes() {
    const labels = this.finishLabelsValue[this.selected("quality")] || []
    this.finishLabelTargets.forEach((el, index) => {
      if (labels[index] != null) el.textContent = labels[index]
    })
  }

  update() {
    const cell = this.currentCell()
    this.refreshCaption(cell)
    this.refreshSizes(cell)
    this.refreshCta(cell)
  }

  refreshCaption(cell) {
    if (!this.hasCaptionTarget) return
    this.captionTarget.textContent = (cell && cell.caption) || ""
  }

  currentCell() {
    const key = `${this.selected("quality")}|${this.selected("finish")}`
    return this.matrixValue[key] || null
  }

  refreshSizes(cell) {
    const sizes = (cell && cell.sizes) || {}
    let selectedStillAvailable = false

    this.sizeTargets.forEach((label) => {
      const input = label.querySelector("input")
      const available = Object.prototype.hasOwnProperty.call(sizes, label.dataset.size)
      input.disabled = !available
      if (input.checked && !available) input.checked = false
      if (input.checked && available) selectedStillAvailable = true

      const priceEl = label.querySelector("[data-size-price]")
      // A disabled size says why instead of going blank.
      if (priceEl) priceEl.textContent = available ? sizes[label.dataset.size].price : this.unavailableLabelValue
    })

    if (!selectedStillAvailable) {
      const firstAvailable = this.sizeTargets.find((label) =>
        Object.prototype.hasOwnProperty.call(sizes, label.dataset.size)
      )
      if (firstAvailable) firstAvailable.querySelector("input").checked = true
    }
  }

  refreshCta(cell) {
    if (!this.hasCtaTarget) return

    const entry = this.currentSizeEntry(cell)
    if (entry) {
      const url = new URL(this.configuratorUrlValue, window.location.origin)
      url.searchParams.set("product_id", cell.product_id)
      url.searchParams.set("variant_id", entry.variant_id)
      this.ctaTarget.setAttribute("href", url.pathname + url.search)
      this.ctaTarget.classList.remove("pointer-events-none", "opacity-50")
    } else {
      this.ctaTarget.removeAttribute("href")
      this.ctaTarget.classList.add("pointer-events-none", "opacity-50")
    }
  }

  track() {
    const cell = this.currentCell()
    const entry = this.currentSizeEntry(cell)
    if (!entry) return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: "quick_print_picker_submit",
      quality: this.selected("quality"),
      finish: (this.finishLabelsValue[this.selected("quality")] || [])[Number(this.selected("finish"))],
      size: this.selected("size"),
      product_id: cell.product_id,
      variant_id: entry.variant_id,
      price: entry.price
    })
  }

  currentSizeEntry(cell) {
    const size = this.selected("size")
    return cell && cell.sizes ? cell.sizes[size] || null : null
  }

  selected(name) {
    const checked = this.element.querySelector(`input[name="${name}"]:checked`)
    return checked ? checked.value : null
  }
}
